import {requestRemoteData} from '../../API/api';
import {isSuccess} from '../../common/domain/RemoteData.model';

const PENDING_ENDPOINT = 'account/products/pending';
const FULFILL_PENDING_ENDPOINT = 'account/products/pending/fulfill';

/**
 * Evento global que se dispara tras cada mutación de pendientes que llega bien
 * al backend. Cada instancia de usePendingOrders (badge del navbar, panel,
 * secciones de la home) lo escucha y se refresca sola: sin esto, añadir un
 * pendiente desde la ficha no movía el contador hasta recargar o abrir el panel.
 */
export const PENDING_ORDERS_CHANGED_EVENT = 'pending-orders-changed';

const notifyIfChanged = (result) => {
  if (isSuccess(result)) {
    window.dispatchEvent(new Event(PENDING_ORDERS_CHANGED_EVENT));
  }
  return result;
};

/**
 * @typedef {Object} PendingOrder
 * @property {Product} product - Producto pendiente.
 * @property {number} pendingQuantity - Unidades pendientes, ya agrupadas y sumadas por el backend.
 * @property {boolean} isRestocked - True cuando ya hay stock para servir pendingQuantity. Lo calcula el backend.
 * @property {string} oldestCreatedAt - Fecha del pendiente más antiguo de ese producto.
 */

const buildHeaders = (actingAsNumCliente) =>
  actingAsNumCliente ? { 'X-Acting-As-Client': actingAsNumCliente } : {};

/**
 * Pedidos pendientes del cliente (o del cliente suplantado en modo comercial).
 * @param {string|null} actingAsNumCliente
 * @returns {Promise<RemoteData<PendingOrder[]>>}
 */
export const fetchPendingOrders = (actingAsNumCliente = null) => {
  return requestRemoteData(
    PENDING_ENDPOINT,
    { headers: buildHeaders(actingAsNumCliente) }
  );
};

/**
 * Guarda como pendientes las unidades que no se pueden servir ahora.
 * @param {string} productCode
 * @param {number} quantity - unidades a dejar pendientes
 * @param {string|null} actingAsNumCliente
 * @returns {Promise<RemoteData<void>>}
 */
export const addPendingOrder = (productCode, quantity, actingAsNumCliente = null) => {
  return requestRemoteData(
    PENDING_ENDPOINT,
    { method: 'POST', queryParams: { code: productCode, quantity }, headers: buildHeaders(actingAsNumCliente) }
  ).then(notifyIfChanged);
};

/**
 * Quita un pendiente a mano (el cliente ya no lo quiere).
 * @param {string} productCode
 * @param {string|null} actingAsNumCliente
 * @returns {Promise<RemoteData<void>>}
 */
export const removePendingOrder = (productCode, actingAsNumCliente = null) => {
  return requestRemoteData(
    PENDING_ENDPOINT,
    { method: 'DELETE', queryParams: { code: productCode }, headers: buildHeaders(actingAsNumCliente) }
  ).then(notifyIfChanged);
};

/**
 * Marca el pendiente como servido. Se llama al añadir el repuesto al carrito.
 * @param {string} productCode
 * @param {string|null} actingAsNumCliente
 * @returns {Promise<RemoteData<void>>}
 */
export const fulfillPendingOrder = (productCode, actingAsNumCliente = null) => {
  return requestRemoteData(
    FULFILL_PENDING_ENDPOINT,
    { method: 'POST', queryParams: { code: productCode }, headers: buildHeaders(actingAsNumCliente) }
  ).then(notifyIfChanged);
};
